<template>
    <section id="images"
             :class="{'card-vertical': cardLayout == 'vertical',
                      'card-horizontal': cardLayout == 'horizontal'}">
        <vue-easy-lightbox
            v-if="showLightbox"
            :visible="visible"
            :imgs="imgs"
            :index="selectedIndex"
            @hide="handleHide"
            @hook:updated="addA11y"
        >
        </vue-easy-lightbox>

        <div class="dot-indicator-container" v-if="showLightbox && visible && images.length > 1">
            <div class="dot-indicator">
                <span v-for="(image, imgIndex) in images" v-bind:key="imgIndex">
                    <label :for="getSelectId(imgIndex)" class="sr-only">{{ $t('labels.lightbox_button_prompt') }} {{ image.title }}</label>
                    <button :id="getSelectId(imgIndex)" :class="{'selected': imgIndex == selectedIndex}" @click="changeImg($event, imgIndex)"></button>
                </span>
            </div>
        </div>

        <div v-if="cardLayout" class="card-image-viewer figure-container">
            <figure v-for="(image, imgIndex) in thumbnails" v-bind:key="imgIndex">
                <span class="mag-icon" @click="showMultiple($event, imgIndex)">
                    <img :alt="image.title" 
                        :src="image.src" 
                        class="lift-hover"
                        @click="showMultiple($event, imgIndex)"
                        @keydown="showMultiple($event, imgIndex)"
                        tabindex="0">
                </span>
            </figure>
        </div>
        <div v-else class="figure-container">
            <figure v-if="hasPlaceholder" class="figure-container">
                <img class="no-recipient-img" :src="placeholder.url" :alt="placeholder.alt">
            </figure>
            <figure v-for="(image, imgIndex) in thumbnails" v-bind:key="imgIndex">
                <span class="mag-icon" @click="showMultiple($event, imgIndex)">
                    <img :alt="image.title" 
                        :src="image.src" 
                        class="lift-hover"
                        @click="showMultiple($event, imgIndex)"
                        @keydown="showMultiple($event, imgIndex)"
                        tabindex="0">
                </span>
                <figcaption>{{image.title}}</figcaption>
            </figure>
        </div>


    </section>
</template>
<script>

export default {
    name: "ImageViewer",
    data: function () {
        return {
            imgs: '',
            visible: true,
            selectedIndex: 0,
            showLightbox: false,
            addListeners: true
        }
    },
    mounted: function () {
        if (process.browser) {
            let VueEasyLightbox = require('vue-easy-lightbox');
        }
    },
    computed: {
        hasImages () {
            return this.images.length > 0 && this.images[0].src;
        },
        hasPlaceholder () {
            return this.placeholder.hasOwnProperty('url');
        }
    },
    props: {
        'images': Array,
        'thumbnails': Array,
        'placeholder': Object,
        'cardLayout': String
    },
    methods: {
        showMultiple: function (e, index) {
            switch(e.type) {
                case 'click':
                    this.show(index);
                case 'keydown':
                    if (e.key !== ' ' && e.key !== 'Enter') return;
                    if (e.key == ' ') e.preventDefault();
                    this.show(index);
                default:
                    return;
            }
        },
        getSelectId: function (index) {
            return `select-${index}`;
        },
        changeImg: function (e, index) {
            switch(e.type) {
                case 'click':
                    this.selectedIndex = index;
                case 'keydown':
                    if (e.key !== ' ' && e.key !== 'Enter') return;
                    if (e.key == ' ') e.preventDefault();
                    this.selectedIndex = index;
                default:
                    return;
            }
        },
        show: function (index) {
            this.showLightbox = true;
            this.imgs = this.images; // an array of image objects {src: 'url', title: 'caption'}
            this.selectedIndex = index;
            this.visible = true;
            this.savedFocus = document.activeElement;

            setTimeout(function () {
                let btn = document.querySelector('.toolbar-btn__zoomin');
                btn.setAttribute('tabindex', -1);
                btn.focus();
                btn.setAttribute('tabindex', 0);
            });
        },
        addA11y: function () {
            // add event listeners to buttons
            let buttons = document.querySelectorAll('.vel-btns-wrapper [class*="btn"]');
            var lightbox = this;

            if (this.addListeners) {
                this.restrictLightboxTabs();
                this.listenForArrows();
            }

            buttons.forEach((btn, index, buttons) => {
                btn.setAttribute('role', 'button');
                btn.setAttribute('tabindex', 0);
                btn.setAttribute('aria-label', this.getLabel(btn, index, buttons));

                if (!btn.getAttribute('hasListeners')) {
                    btn.addEventListener('keydown', function (e) {
                        if (e.key == ' ' || e.key == 'Enter') {
                            e.preventDefault();
                            this.click();
                        }
                    });

                    if (btn.classList.contains('toolbar-btn__zoomin') || btn.classList.contains('toolbar-btn__zoomout')) {
                        btn.addEventListener('click', function (e) {
                            this.click();
                            this.click();
                        });
                    }

                    if (btn.classList.contains('btn__next') || btn.classList.contains('btn__prev')) {
                        btn.addEventListener('click', function (e) {
                            if (this.classList.contains('btn__next') && lightbox.selectedIndex < lightbox.imgs.length - 1) {
                                lightbox.selectedIndex = lightbox.selectedIndex + 1;
                            } else if (this.classList.contains('btn__prev') && lightbox.selectedIndex > 0 ) {
                                lightbox.selectedIndex = lightbox.selectedIndex - 1;
                            }
                        });
                    }
                }

                btn.setAttribute('hasListeners', true);

                // add aria attributes
                if (document.querySelector('.vel-img-title')) {
                    document.querySelector('.vel-img-title').id = 'img-title';
                }

                setTimeout(function () {
                    if (document.querySelector('.vel-img')) {
                        document.querySelector('.vel-img').setAttribute('aria-describedby', 'img-title');
                    }
                }, 800)

                document.querySelector('.vel-img-modal').setAttribute('aria-live', 'polite');
                document.querySelector('.vel-img-modal').setAttribute('aria-modal', 'true');
            });

            lightbox.addListeners = false;
        },
        getLabel: function (btn, index, buttons) {
            let translatedLabels = this.$i18n.messages[this.$i18n.locale].labels;
            var labels = {
                btn__prev: translatedLabels['lightbox_prev'],
                btn__next: translatedLabels['lightbox_next'],
                btn__close: translatedLabels['lightbox_close'],
                toolbar_btn__zoomin: translatedLabels['lightbox_zoomin'],
                toolbar_btn__zoomout: translatedLabels['lightbox_zoomout'],
                toolbar_btn__resize: translatedLabels['lightbox_resize'],
                toolbar_btn__rotate: translatedLabels['lightbox_rotate_left'],
                toolbar_btn__rotate_right: translatedLabels['lightbox_rotate_right'],
            };
            var label = '';

            if (index < buttons.length - 1) {
                btn.classList.forEach(cls => {
                    if (cls.includes('btn__')) {
                        label = labels[cls.replace('-', '_')];
                    }
                });
            } else {
                // handle case where both rotate buttons have same class
                btn.setAttribute('data-rotate', 'right');
                label = labels['toolbar_btn__rotate_right'];
            }

            return label;
        },
        listenForArrows: function () {
            //  listen for arrow keypress
            let lightbox = this;
            let btnWrapper = document.querySelector('#images');
            btnWrapper.addEventListener('keydown', e => {
                if (e.key == 'ArrowRight' && (lightbox.selectedIndex < lightbox.imgs.length - 1)) {
                    lightbox.selectedIndex = lightbox.selectedIndex + 1;
                } else if (e.key == 'ArrowLeft' && lightbox.selectedIndex > 0 ) {
                    lightbox.selectedIndex = lightbox.selectedIndex - 1;
                }
            });
        },
        restrictLightboxTabs: function () {
            let btnWrapper = document.querySelector('#images');
            let lightbox = this;
            let keysPressed = {};

            btnWrapper.addEventListener('keydown', function (e) {
                keysPressed[e.key] = true;

                let firstButton = e.target.classList.contains('btn__prev');
                let lastButton = e.target.id == `${lightbox.getSelectId(lightbox.images.length - 1)}`;

                if (lightbox.images.length == 1) {
                    firstButton = e.target.classList.contains('btn__close');
                    lastButton = e.target.getAttribute('data-rotate') == 'right';
                }

                if (firstButton) {
                    if (keysPressed['Tab'] && keysPressed['Shift']) {
                        e.preventDefault();
                    };
                } else if (lastButton) {
                    if (keysPressed['Tab'] && !keysPressed['Shift']) {
                        e.preventDefault();
                    };
                }
            });

            btnWrapper.addEventListener('keyup', e => {
                if (keysPressed[e.key]) keysPressed[e.key] = false;
            });
        },
        handleHide: function () {
            this.savedFocus.focus();
            this.visible = false;
        }
    }
}
</script>

<style lang="scss">

#images {

    max-width: 420px;
    width: 45%;

    height: fit-content;

    &.card-vertical {
        max-width: initial;
        width: 100%;
        height: 200px;

        figure {
            height: 200px;
            width: auto;
            margin: 0;

            img.lift-hover {
                width: auto;
                @supports (-ms-ime-align: auto) {
                    width: 100%;
                }
                height: 100%;
            }
        }
    }
    &.card-horizontal {
        width: 100%;
        height: 100%;

        figure, img.lift-hover, .figure-container {
            height: 100%;
        }

        figure {
            width: 45%;

            margin: 10px 0;
            padding: 0;
        }

        img.lift-hover {
            width: auto;
            @supports (-ms-ime-align: auto) {
                width: 100%;
            }
            margin-top: -10px;
            max-width: 400px;
        }
    }

    figure {
        width: 45%;
        margin: 10px 0;
        padding: 0;
        
        .mag-icon {
            position: relative;

            &:after {
                content: url('../assets/icons/icon-zoom-in.svg');

                width: 38px;
                height: 25px;
                display: block;
                position: absolute;
                bottom: 20px;
                right: 0;
            }
        }
    }

    figure:first-of-type {
        width: 100%;
    }

    .figure-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    figcaption {
        margin-top: 7px;
        line-height: 22px;
        font-size: 14px;
    }

    figure img {
        cursor: pointer;
        width: 100%;

        &.no-recipient-img {
            cursor: initial;
        }
    }


    .dot-indicator-container {
        z-index: 9998;
        position: fixed;
        bottom: 40px;
        right: 0;
        left: 0;


        .dot-indicator {
            z-index: 10000;
            text-align: center;
        }
        span {
            background:  rgba(0,0,0,.5);
            padding: 10px 5px 5px;
        }

        button {
            display: inline-block;
            width: 16px;
            height: 16px;
            background-color: #c0c0c0;
            border-radius: 50%;
            border: none;
            margin: 0 15px;
            cursor: pointer;

            &.selected {
                background-color: #00b74f;
            }

            &:focus {
                outline: solid #92b2cf 1px;
            }
        }
    }
    .vel-img-modal.vel-modal {
        background-color:  rgba(0,0,0,.88);

        img.vel-img {
            width: auto;
            margin-top: -140px;
            transform: scale(.8);
        }

        .vel-btns-wrapper {
            
            [class*="btn"]:focus {
                outline: #92b2cf solid 1px;
            }

            .vel-img-title {
                font-size: 14px;
                max-width: 350px;
                bottom: 140px;
                line-height: 1.4;
                text-overflow: initial;
                white-space: initial;
                opacity: 1;
                background:  rgba(0,0,0,.5);
                padding: 10px;
            }

            .btn__close,
            .btn__next,
            .btn__prev {
                opacity: 1;
                font-size: 45px;
                padding: 5px;
                background:  rgba(0,0,0,.5);

                &.disable {
                    opacity: .3;
                }
            }

            .btn__next { right: 10%; }
            .btn__prev { left: 10%; }
            .btn__close {
                font-size: 30px;
                top: 8%;
                right: 5%;
            }

            .vel-toolbar {
                border-radius: 0;
                bottom: 80px;
                opacity: 1;
                font-size: 22px;
                overflow: visible;
                background-color: transparent;

                .toobar-btn {
                    background-color: rgba(0,0,0,.5);
                    padding: 10px 22px;
                    margin: 0 5px;
                    svg {
                        color: #C0C0C0;
                        &:hover { color: white; }
                    }
                    &:first-child { margin-left: 0;}
                    &:last-child { margin-right: 0;}
                }
            }
        }
    }
}

@media screen and (max-width: 1080px) {
    #images {
        max-width: 400px;
    }
}


@media screen and (max-width: 868px) {

    #images {
        max-width: initial;
        width: 100%;
        figure {
            width: 30%;
        }
    }
}

@media screen and (max-width: 500px) {
    #images {
        figure {
            width: 45%;
        }
    }
}
</style>